// Rollenmodell der Eigentümer (Handoff Eigentümer §5) – reine Funktionen, testbar.
// Die Rolle je Zuordnung wird aus vorhandenen Daten abgeleitet (Freigabe 13.10.2026):
// objekte.verwaltungsart MV/GV → MV · WEG ohne SEV → WEG · WEG mit einheit_eigentuemer.sev_status → WEG+SEV ·
// verwaltungsart SEV (WEG fremdverwaltet) → SEV mit externem Verwalter.

import type { VerwaltungsartKurz } from '../immobilien/sidebarNav';

export type Rolle = 'MV' | 'WEG' | 'SEV' | 'WEG+SEV';
export type RolleKurz = 'MV' | 'WEG' | 'SEV';

export function rolleFuer(verwaltungsart: VerwaltungsartKurz | null, sevStatus: string | null | undefined): Rolle {
  if (verwaltungsart === 'WEG') return (sevStatus ?? '').trim() ? 'WEG+SEV' : 'WEG';
  if (verwaltungsart === 'SEV') return 'SEV';
  return 'MV';
}

/** WEG, die nicht wir verwalten (Objekt mit Verwaltungsart SEV): Hausgeld geht an den Fremdverwalter. */
export function istFremdverwaltet(verwaltungsart: VerwaltungsartKurz | null): boolean {
  return verwaltungsart === 'SEV';
}

export const ROLLE_LANG: Record<Rolle, string> = {
  MV: 'Mietverwaltung (ganzes Haus)',
  WEG: 'WEG-Mitglied',
  SEV: 'SEV · WEG fremdverwaltet',
  'WEG+SEV': 'WEG-Mitglied + Sondereigentumsverwaltung',
};

export interface ZuordnungEinheit {
  id: string;
  bezeichnung: string;
  flaeche: number;
  /** Hausgeld-Vorschuss inkl. Rücklage je Monat (einheit_eigentuemer) */
  hausgeld: number;
  mea: number | null;
  /** Gesamtmiete der laufenden Mieter */
  miete: number;
  vermietet: boolean;
}

export interface Zuordnung {
  objektId: string;
  objektName: string;
  rolle: Rolle;
  /** Eigentum am ganzen Objekt (objekt_eigentuemer) statt einzelner Einheiten */
  ganzesObjekt: boolean;
  einheiten: ZuordnungEinheit[];
  ab: string | null;
  bis: string | null;
}

export const istWegUns = (r: Rolle): boolean => r === 'WEG' || r === 'WEG+SEV';
export const istSev = (r: Rolle): boolean => r === 'SEV' || r === 'WEG+SEV';
/** Vertragspartner ist der Eigentümer selbst (Verwaltervertrag MV/SEV). */
export const istVerwaltet = (r: Rolle): boolean => r === 'MV' || istSev(r);

const summe = <T>(liste: readonly T[], f: (x: T) => number): number => Math.round(liste.reduce((s, x) => s + f(x), 0) * 100) / 100;

export interface RollenWerte {
  hasMV: boolean;
  hasWEG: boolean;
  hasSEV: boolean;
  verwaltet: boolean;
  /** WEG fremdverwaltet beteiligt (Panel „WEG-Verwalter (extern)“) */
  extern: boolean;
  rollen: RolleKurz[];
  rollenL: string;
  nEinheiten: number;
  nVerwaltet: number;
  flaeche: number;
  /** Hausgeld unserer WEGs */
  hgM: number;
  /** davon aus der SEV-Miete verrechnet (WEG+SEV) */
  hgSevUns: number;
  /** Hausgeld an Fremdverwalter, aus der Miete überwiesen */
  hgExt: number;
  hgSev: number;
  /** zahlt der Eigentümer selbst per SEPA */
  direktHg: number;
  miete: number;
  /** 5 % Instandhaltungsrücklage nur bei MV */
  instM: number;
  /** Verwaltervergütung (nur MV/SEV); null = nicht hinterlegt */
  vergM: number | null;
  /** Auszahlung = Miete − Hausgeld aus Miete − Vergütung − Rücklage */
  ausz: number;
  saldoArt: 'hausgeld' | 'konto';
  /** Betrag für Liste/Kachel: Hausgeld oder Auszahlung */
  monatlich: number;
}

export function rollenWerte(zuordnungen: readonly Zuordnung[], verguetungJeEinheit: number | null): RollenWerte {
  const hasMV = zuordnungen.some(z => z.rolle === 'MV');
  const hasWEG = zuordnungen.some(z => istWegUns(z.rolle));
  const hasSEV = zuordnungen.some(z => istSev(z.rolle));
  const verwaltet = hasMV || hasSEV;
  const extern = zuordnungen.some(z => z.rolle === 'SEV');
  const rollen = ([hasMV && 'MV', hasWEG && 'WEG', hasSEV && 'SEV'] as const).filter((r): r is RolleKurz => !!r);
  const alle = zuordnungen.flatMap(z => z.einheiten);
  const verwalteteZ = zuordnungen.filter(z => istVerwaltet(z.rolle));
  const nVerwaltet = verwalteteZ.reduce((s, z) => s + z.einheiten.length, 0);
  const hg = (f: (z: Zuordnung) => boolean) => summe(zuordnungen.filter(f), z => summe(z.einheiten, e => e.hausgeld));
  const hgM = hg(z => istWegUns(z.rolle));
  const hgSevUns = hg(z => z.rolle === 'WEG+SEV');
  const hgExt = hg(z => z.rolle === 'SEV');
  const hgSev = Math.round((hgSevUns + hgExt) * 100) / 100;
  const direktHg = Math.round((hgM - hgSevUns) * 100) / 100;
  const miete = summe(verwalteteZ, z => summe(z.einheiten, e => e.miete));
  const instM = Math.round(summe(zuordnungen.filter(z => z.rolle === 'MV'), z => summe(z.einheiten, e => e.miete)) * 0.05);
  const vergM = verguetungJeEinheit === null || !verwaltet ? (verwaltet ? null : 0) : Math.round(verguetungJeEinheit * nVerwaltet * 100) / 100;
  const ausz = Math.round((miete - hgSev - (vergM ?? 0) - instM) * 100) / 100;
  const saldoArt = direktHg > 0 ? 'hausgeld' : 'konto';
  return {
    hasMV, hasWEG, hasSEV, verwaltet, extern, rollen, rollenL: rollen.join(' · '),
    nEinheiten: alle.length, nVerwaltet, flaeche: summe(alle, e => e.flaeche),
    hgM, hgSevUns, hgExt, hgSev, direktHg, miete, instM, vergM, ausz, saldoArt,
    monatlich: saldoArt === 'hausgeld' ? direktHg : verwaltet ? ausz : hgM,
  };
}

// ── Sichtbarkeit (README §5) ───────────────────────────────────────────────────

export type EigPanel =
  | 'portfolio' | 'objekte' | 'beteiligte' | 'zustellung' | 'termine' | 'bank' | 'kontakt' | 'portal' | 'kommunikation' | 'dokumente' | 'briefe' | 'notizen' | 'ki'
  | 'konto' | 'mieter' | 'abrechnung' | 'vertrag' | 'freigaben' | 'steuer'
  | 'hausgeld' | 'sondereigentum' | 'wegabrechnung' | 'ruecklage' | 'etv' | 'wegverwalter'
  | 'wegext';

const IMMER: readonly EigPanel[] = ['portfolio', 'objekte', 'beteiligte', 'zustellung', 'termine', 'bank', 'kontakt', 'portal', 'kommunikation', 'dokumente', 'briefe', 'notizen', 'ki'];
const VERWALTET: readonly EigPanel[] = ['konto', 'mieter', 'abrechnung', 'vertrag', 'freigaben', 'steuer'];
const WEG: readonly EigPanel[] = ['hausgeld', 'sondereigentum', 'wegabrechnung', 'ruecklage', 'etv', 'wegverwalter'];

export function panelSichtbar(p: EigPanel, w: Pick<RollenWerte, 'verwaltet' | 'hasWEG' | 'extern'>): boolean {
  if (IMMER.includes(p)) return true;
  // Steuer auch für WEG-Mitglieder: § 35a-Bescheinigung und Anteil an den WEG-Kosten (#564 S5d)
  if (p === 'steuer') return w.verwaltet || w.hasWEG;
  if (VERWALTET.includes(p)) return w.verwaltet;
  if (WEG.includes(p)) return w.hasWEG;
  return p === 'wegext' && w.extern;
}

export type EigGruppe = 'alle' | 'port' | 'fin' | 'weg' | 'verw' | 'komm';

/** Gruppenfilter: Alle · Portfolio · Finanzen · WEG (wenn WEG/extern) · Verwaltung (wenn verwaltet) · Kommunikation. */
export function gruppen(w: Pick<RollenWerte, 'verwaltet' | 'hasWEG' | 'extern'>): { key: EigGruppe; label: string }[] {
  return [
    { key: 'alle', label: 'Alle' },
    { key: 'port', label: 'Portfolio' },
    { key: 'fin', label: 'Finanzen' },
    ...(w.hasWEG || w.extern ? [{ key: 'weg' as const, label: 'WEG' }] : []),
    ...(w.verwaltet ? [{ key: 'verw' as const, label: 'Verwaltung' }] : []),
    { key: 'komm', label: 'Kommunikation' },
  ];
}
